import React, { useCallback, useRef, useEffect, useState, useMemo, useContext } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import ReactDOM from 'react-dom';
import { withTranslation, useTranslation } from 'react-i18next';
import { HtmlBlockContainer, QuillContainer, HtmlDefaultPlaceholder } from './styles';
import { COMPLEX_DOCUMENT_CONTEXT_KEYS, useDocumentContext } from '../../context/complexDocumentContext';
import {
  useArtboardContext,
  CONTEXT_KEYS,
  TEMPLATE_TYPE,
} from '../../context/complexArtboardContext';
import { dummyCallback, HTML_BLOCK } from '../../utils/complexDocumentConstants';
import TextBlockToolbar from './TextBlockToolbar';
import { getElementBySelectorAsync } from './complexDocumentHelper';
import BlockHighlight from './BlockHighlight';
import PageElementsProvider from '../../context/pageElementsContext';
import { useDefaultStyleContext } from '../../context/complexDefaultStyleContext';
import CustomQuillHistory from './CustomQuillHistoryModule';
import { ComplexTextEditingContext } from '../../context/complexTextEditingContext';
import _ from 'underscore';
import { COMPLEX_PAGE_OPTIONS } from './translationValues';
import { HtmlClipboard } from './HtmlQuillClipboardModule';

const Delta = Quill.import('delta');
const Parchment = Quill.import('parchment');

class UnderlineStyleAttributor extends Parchment.Attributor.Style {
  add(node, value) {
    if (value === true) return super.add(node, 'underline');
    return super.add(node, value);
  }
  value(node) {
    return super.value(node) === 'underline' ? true : undefined;
  }
};

const HtmlBlockRenderer = props => {
  const {
    editable,
    itemUUID,
    parentUUID,
    type,
    isMobileView = false,
    onClickBlockSelection = dummyCallback,
    getElementStyles,
    showVersionSettings
  } = props;
  const blockRef = useRef(null);
  const [htmlEditable, setHtmlEditable] = useState(false);
  const { t } = useTranslation(['COMPLEX_PAGE_OPTIONS']);
  const customSelector = useCallback(store => store[itemUUID], []);
  const [htmlBlock, methods] = useDocumentContext(customSelector, `htmlBlock-${itemUUID}`);

  const dataSetSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.DATA_SET], []);
  const [dataSet] = useDocumentContext(dataSetSelector, `htmlBlock-${itemUUID}`);

  const interactiveDesignSelector = useCallback(store => store[CONTEXT_KEYS.IS_INTERACTIVE_DESIGN], []);
  const [isInteractiveDesign, updateArtboard] = useArtboardContext(interactiveDesignSelector, `HtmlBlockRenderer`);

  const htmlContent = dataSet?.[htmlBlock?.variableContract?.[0]?.name] ?? null;

  const artboardContextSelector = useCallback(
    store => ({
      currentBlock: store[CONTEXT_KEYS.CURRENT_BLOCK] === itemUUID,
      selectedBlocks: store[CONTEXT_KEYS.SELECTED_BLOCKS],
      isDocumentOrFlowDesign: [TEMPLATE_TYPE.DOCUMENT, TEMPLATE_TYPE.FLOW].includes(
        store[CONTEXT_KEYS.CURRENT_DESIGN],
      ),
    }),
    [],
  );
  const [
    { currentBlock, selectedBlocks, isDocumentOrFlowDesign }
  ] = useArtboardContext(artboardContextSelector, `htmlBlock-${itemUUID}`);

  const handleSingleclick = e => {
    if (editable && isDocumentOrFlowDesign && !currentBlock) {
      if (e.ctrlKey || e.metaKey) {
        onClickBlockSelection({ itemUUID, parentUUID });
      }
    }
  };

  const handleDoubleClick = () => {
    if (htmlBlock.isEditable) {
      setHtmlEditable(true);
      return;
    }
    if (editable) {
      const target = document.getElementById(`settings-tooltip-${itemUUID}`);
      target && target.click();
    }
  };

  const updateSidebar = useCallback(() => {
    if (!editable) return;
    if (currentBlock) return;
    if (htmlBlock.isEditable) {
      // setHtmlEditable(true);
      updateArtboard({
        [CONTEXT_KEYS.CURRENT_BLOCK]: itemUUID,
        [CONTEXT_KEYS.CURRENT_BLOCK_TYPE] : HTML_BLOCK,
        [CONTEXT_KEYS.CURRENT_PAGE]: parentUUID,
        [CONTEXT_KEYS.PAGE_LAYOUT_VISIBLE]: false,
        [CONTEXT_KEYS.BLOCK_SETTINGS_VISIBLE]: false
      });
      return;
    } else {
      const target = document.getElementById(`settings-tooltip-${itemUUID}`);
      target && target.click();
    }
  }, [itemUUID, parentUUID, currentBlock, editable, htmlBlock]);

  const blockSelected = selectedBlocks.includes(itemUUID);

  // Get HTML content from htmlBlock.content (as specified by user)
  const defaultHtml = (typeof htmlBlock?.contents === 'string' ? htmlBlock?.contents : null) || `<p style="text-align: left"> <br/> </p>`;

  // Default HTML template to compare against
  const DEFAULT_HTML_TEMPLATE = `<p style="text-align: left"> <br/> </p>`;

  // Detect if current content is default/unmodified
  const isDefaultContent = useMemo(() => {
    if (!htmlBlock?.isEditable) return false;
    const currentContent = (typeof htmlBlock?.contents === 'string' ? htmlBlock?.contents : undefined) || '';
    // Normalize whitespace for comparison
    const normalizedCurrent = currentContent.trim().replace(/\s+/g, ' ');
    const normalizedDefault = DEFAULT_HTML_TEMPLATE.trim().replace(/\s+/g, ' ');
    
    // Check if content matches default exactly or is essentially empty
    const value = normalizedCurrent === normalizedDefault ||
           normalizedCurrent === '' || 
           normalizedCurrent === '<p></p>' ||
           normalizedCurrent === '<p><br></p>' ||
           normalizedCurrent === '<p><br/></p>';
    return value;
  }, [currentBlock, htmlBlock?.isEditable, htmlBlock?.contents]);

  // Save function with history support
  const saveHtmlContent = useCallback((html, undoDiff, redoDiff, cb, ref, op = undefined) => {
    console.log('Saving HTML content for block:', itemUUID, html, 'undoDiff:', undoDiff, 'redoDiff:', redoDiff);
    methods.updateBlock('html-contents', itemUUID, {
      contents: html,
      type: 'block',
      flowUUID: parentUUID,
      isInteractiveDesign: isInteractiveDesign,
      historyChange: {
        undo: undoDiff,
        redo: redoDiff,
        op: op
      }
    }, true);
    // Call the callback after save to reset history reference
    if (cb) {
      cb();
    }
  }, [itemUUID, methods, parentUUID, isInteractiveDesign]);

  // Convert to Quill editor - similar to TextBlockWrapper's convertToQuill
  const convertToQuill = useCallback((editable) => {
    setHtmlEditable(editable);
  }, []);

  return (
    <HtmlBlockContainer
      id={itemUUID}
      className={isInteractiveDesign || htmlBlock?.isEditable ? 'empty' :'empty'}
      currentBlock={currentBlock}
      style={{
        height: htmlBlock?.isEditable ? 'auto' : htmlBlock?.style?.height * (isMobileView ? 0.66 : 1),
        background: blockSelected ? '#ACCEF7' : htmlBlock?.style?.backgroundColor,
      }}
      onClick={handleSingleclick}
      onDoubleClick={updateSidebar}
      onMouseMove={e =>
        (editable || isInteractiveDesign || showVersionSettings) && !currentBlock && getElementStyles && getElementStyles(e, itemUUID)
      }
    >
      {htmlBlock?.isEditable ? (
        <PageElementsProvider>
        {editable && isDefaultContent && (
          <HtmlDefaultPlaceholder isEditing={currentBlock}>
            {t(`COMPLEX_PAGE_OPTIONS:${COMPLEX_PAGE_OPTIONS.HTML_DEFAULT_CONTENT_PROMPT.key}`, COMPLEX_PAGE_OPTIONS.HTML_DEFAULT_CONTENT_PROMPT.value)}
          </HtmlDefaultPlaceholder>
        )}
        <HtmlBlockQuillRenderer
          ref={blockRef}
          saveBlock={saveHtmlContent}
          defaultHtml={defaultHtml}
          itemUUID={itemUUID}
          selected={currentBlock}
          backgroundColor={blockSelected ? '#ACCEF7' : htmlBlock?.style?.backgroundColor}
          padding={htmlBlock?.style?.padding || { left: 0, right: 0, top: 0, bottom: 0 }}
          editable={editable}
          onMouseMove={e =>
            (editable || isInteractiveDesign || showVersionSettings) && !currentBlock && getElementStyles && getElementStyles(e, itemUUID)
          }
          textEditable={htmlEditable}
          setTextEditable={convertToQuill}
          customClassName={'htmlEditor'}
          lineSpacing={htmlBlock?.style?.lineSpacing}
          paragraphSpacing={htmlBlock?.style?.paragraphSpacing}
          alignment={htmlBlock?.style?.alignment}
        >
          {currentBlock && htmlBlock.style.backgroundColor === undefined && (
             <BlockHighlight pageUUID={parentUUID} />
          )}
        </HtmlBlockQuillRenderer>
        </PageElementsProvider>
      ) : 'HTML </>  '}
      {/*isInteractiveDesign && htmlContent && <div dangerouslySetInnerHTML={{ __html: htmlContent }} />*/}
    </HtmlBlockContainer>
  );
};

const DEBOUNCE_TIME_OUT = 300;

const FORMATS = [
  'font',
  'size',
  'bold',
  'italic',
  'underline',
  'list',
  'bullet',
  'align',
  'indent',
  'link',
  'color',
  'background',
  'lineheight',
  'letterspace',
  'textTransform',
  'script'
];

const HtmlBlockQuillRenderer = React.forwardRef(({
  saveBlock,
  defaultHtml = '',
  itemUUID,
  selected,
  children,
  adjustScroll = dummyCallback,
  enableSideBar = dummyCallback,
  onContainerSingleClick = dummyCallback,
  onContainerClick = dummyCallback,
  backgroundColor = 'transparent',
  customClassName = '',
  padding = {
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },
  scaling = 1,
  isPlaceholderVisible = false,
  isFontSidebarAbsolute = false,
  isPreview = false,
  toolbarDOM = 'document-editor-toolbar',
  noFocus = false,
  invisible = false,
  editable = true,
  toolbarInvisible = false,
  onMouseMove = dummyCallback,
  textEditable = true,
  setTextEditable = dummyCallback,
  exposeRef = true,
  getEditorContentLength,
  customBlurHandler = dummyCallback,
  border_radius = '0px',
  lineSpacing,
  paragraphSpacing,
  alignment = ''
}, ref) => {
  const editorRef = useRef(null);
  const parentStyleRef = useRef(null);
  const historyRef = useRef({ undo: 0, redo: 0, op: undefined });
  const [readOnly, setReadOnly] = useState(true);
  const [htmlContent, setHtmlContent] = useState(defaultHtml);
  const [forceRender, toggleToolbarForceRender] = useState(false);
  const { t } = useTranslation(['COMPLEX_PAGE_OPTIONS']);
  const editorMap = useContext(ComplexTextEditingContext);

  const defaultHtmlRef = useRef(defaultHtml);

  useEffect(() => {
    defaultHtmlRef.current = defaultHtml;
    if (defaultHtml !== htmlContent) {
      setHtmlContent(defaultHtml);
    }
  }, [defaultHtml]);

  const debouncedSendRequest = useCallback(_.debounce(saveBlock, DEBOUNCE_TIME_OUT), [saveBlock]);

  const getDefaultStyleSelector = useCallback(store => ({fontFamily: store.fontFamily, fontSize:store.fontSize}), []);
  const [documentStyle] = useDefaultStyleContext(getDefaultStyleSelector);

  // History callbacks for CustomQuillHistory module
  const onChange = useCallback((changeType, undoDiff, redoDiff) => {
    historyRef.current.op = changeType;
    if (changeType === 'record') {
      historyRef.current.undo = undoDiff;
      historyRef.current.redo = redoDiff;
    }
  }, []);

  const onHistoryChange = useCallback((historyStack) => {
    if (editorMap?.current) {
      editorMap.current.set(itemUUID, historyStack);
    }
  }, [itemUUID, editorMap]);

  useMemo(() => {
    // Register CustomQuillHistory module
    Quill.register('modules/history', CustomQuillHistory, true);
    Quill.register('modules/clipboard', HtmlClipboard, true);
    const UnderlineStyle = new UnderlineStyleAttributor('underline', 'text-decoration', {
      scope: Parchment.Scope.INLINE,
      whitelist: ['underline', 'none']
    });
    Quill.register(UnderlineStyle, true);
  }, []);

  const quillContentChangeHandler = (html, delta, source) => {
    if (!editorRef.current || source !== 'user') return;
    const editor = editorRef?.current.getEditor();
    if (getEditorContentLength) {
      getEditorContentLength(editor.getLength());
    }
    setHtmlContent(html);
    debouncedSendRequest(
      html,
      historyRef.current.undo,
      historyRef.current.redo,
      () => {
        // Reset history reference after save
        historyRef.current = { undo: 0, redo: 0, op: undefined };
        parentStyleRef.current = undefined;
      },
      parentStyleRef.current,
      historyRef.current.op
    );
    adjustScroll();
  };

  const quillContentSelectionHandler = (range, source) => {
    if (source === 'silent' || !range) {
      return;
    }
    if (source === 'user') {
      toggleToolbarForceRender(currentValue => !currentValue);
    }
  };

  const quillFocusHandler = (e, source) => {
    console.log("HTML editor focused!!");
    if (source === 'user') {
      if (editorRef.current) {
        const editor = editorRef?.current.getEditor();
      }
    } else if (source === 'api' && invisible) {
      const editor = editorRef?.current.getEditor();
      editor.setSelection(0, editor.getLength());
    }
  };

  const quillBlurHandler = (e, source) => {
    console.log("HTML editor blurred!!");
    if (customBlurHandler) customBlurHandler();
  };

  useEffect(() => {
    if (!selected) {
      setReadOnly(true);
      const editor = editorRef?.current?.getEditor();
      if (editor) {
        editor.blur();
      }
    }
  }, [selected]);

  const MODULES = useMemo(() => {
    return {
      toolbar: {
        container: `#toolbar-${itemUUID}`,
        handlers: {
          link: function (value) {
            const range = this.quill.getSelection();
            if (range == null || range.length === 0) return;
            const contents = this.quill.getContents(range.index, range.length);
            const preview = contents.ops
              .map(item => item.insert)
              .join('')
              .trim();
            if (value) {
              if (/^\S+@\S+\.\S+$/.test(preview) && preview.indexOf('mailto:') !== 0) {
                const tooltip = this.quill.theme.tooltip;
                tooltip.edit('link', 'mailto:' + preview);
              } else {
                const tooltip = this.quill.theme.tooltip;
                tooltip.edit('link', preview);
              }
            } else {
              this.quill.format('link', false, 'user');
            }
          }
        },
      },
      keyboard: {
        bindings: {
          tab: {
            key: 9,
            handler: function handler(range) {
              const formats = this.quill.getFormat(range.index);
              const delta = new Delta()
                .retain(range.index)
                .delete(range.length)
                .insert('    ', formats);
              this.quill.updateContents(delta, 'user');
              this.quill.setSelection(range.index + 4, 'silent');
            }
          },
        }
      },
      clipboard: {
        matchVisual: false,
      },
      history: {
        userOnly: true,
        delay: 3000,
        maxStack: 999999,
        callback: onChange,
        historyCallback: onHistoryChange,
        itemUUID,
        customize: true,
      },
    };
  }, [itemUUID, onChange, onHistoryChange]);

  // Restore history stack if it exists
  useEffect(() => {
    const existingStack = editorMap?.current?.get(itemUUID);
    if (existingStack) {
      if (editorRef.current) {
        const editor = editorRef.current.getEditor();
        editor.history.setInitialStack(existingStack);
      } else {
        setTextEditable(true);
        requestAnimationFrame(() => {
          const editor = editorRef?.current?.getEditor();
          if (editor) {
            editor.history.setInitialStack(existingStack);
          }
        });
      }
    }
  }, [itemUUID, editorMap]);

  const setCursorOnClick = (event, editor) => {
    if (!editable) return;
    const { clientX, clientY, reAddAreaElementStyles, removeAreaElementStyles } = event;
    !textEditable && removeAreaElementStyles && removeAreaElementStyles();
    let range;
    if (document.caretPositionFromPoint) {
      // for firefox
      range = document.caretPositionFromPoint(clientX, clientY);
      range && editor && editor.selection.setNativeRange(range.offsetNode, range.offset);
    } else if (document.caretRangeFromPoint) {
      // for chrome and other browsers
      range = document.caretRangeFromPoint(clientX, clientY);
      range && editor && editor.selection.setNativeRange(range.startContainer, range.startOffset);
    }
    console.log("setCursorOnClick called :: ", range);
    !textEditable && reAddAreaElementStyles && reAddAreaElementStyles();
  };

  const editor = editorRef?.current?.getEditor();

  const quillContainerDoubleClick = event => {
    if (!editable) return;
    if (!isPreview && !editor?.hasFocus()) {
      console.log("quillContainerDoubleClick called :: ", editable, textEditable, editor);
      setReadOnly(false);
        const cb = setTextEditable(true);
        event.persist();
        requestAnimationFrame(() => {
          if (cb) {
            cb();
          }
          const editor = editorRef?.current.getEditor();
          setCursorOnClick(event, editor);
          editor.focus();
          toggleToolbarForceRender(currentValue => !currentValue);
        });
    }
    onContainerClick();
  };

  if (isPreview || !textEditable) {
    return (
      <QuillContainer
        editable={editable}
        ref={ref}
        id={`q-container-${itemUUID}`}
        data-uuid={itemUUID}
        style={{
          position: 'relative',
          zIndex: selected ? 2 : 0,
          overflow: selected ? 'visible' : 'hidden',
          minHeight: 'fit-content',
          backgroundColor: backgroundColor,
          paddingTop: padding.top,
          paddingBottom: padding.bottom,
          paddingLeft: padding.left,
          paddingRight: padding.right,
          display: invisible ? 'none' : 'block',
          borderRadius: border_radius,
        }}
        className={`${customClassName} preview html ${selected ? 'selected' : 'no-cursor'}`}
        onClick={onContainerSingleClick}
        onDoubleClick={quillContainerDoubleClick}
        onMouseMove={onMouseMove}
        documentStyle={documentStyle}
        lineSpacing={lineSpacing}
        paragraphSpacing={paragraphSpacing}
        alignment={alignment}
      >
        <div className='quill'>
          <div className='ql-container ql-snow ql-preview'>
            {children}
            {htmlContent && <div className='ql-editor' dangerouslySetInnerHTML={{ __html: htmlContent }} />}
          </div>
        </div>
      </QuillContainer>
    );
  }

  return (
    <QuillContainer
      editable={editable}
      ref={ref}
      id={`q-container-${itemUUID}`}
      data-uuid={itemUUID}
      style={{
        position: 'relative',
        zIndex: selected ? 2 : 0,
        overflow: selected ? 'visible' : 'hidden',
        minHeight: 'fit-content',
        backgroundColor: backgroundColor,
        paddingTop: padding.top,
        paddingBottom: padding.bottom,
        paddingLeft: padding.left,
        paddingRight: padding.right,
        display: invisible ? 'none' : 'block',
        borderRadius: border_radius,
      }}
      className={`${customClassName} html ${selected ? 'selected' : 'no-cursor'}`}
      onClick={onContainerSingleClick}
      onDoubleClick={quillContainerDoubleClick}
      onMouseMove={onMouseMove}
      documentStyle={documentStyle}
      lineSpacing={lineSpacing}
      paragraphSpacing={paragraphSpacing}
      alignment={alignment}
    >
      {children}
      {document.getElementById(toolbarDOM) && (
        <>
          {ReactDOM.createPortal(
            <TextBlockToolbar
              isToolBarVisible={!readOnly && noFocus ? true : editor?.hasFocus()}
              itemUUID={itemUUID}
              editorRef={editorRef}
              enableSideBar={enableSideBar}
              forceRender={forceRender}
              containerRef={parentStyleRef}
              isAbsolute={isFontSidebarAbsolute}
              invisible={invisible || itemUUID.endsWith('-row')}
              toolbarInvisible={toolbarInvisible}
              isTocTitleBlock={false}
              isHTMLBlock={true}
              defaultStyles={documentStyle}
            />,
            document.getElementById(toolbarDOM),
          )}
          <ReactQuill
            ref={editorRef}
            modules={MODULES}
            formats={FORMATS}
            readOnly={readOnly}
            defaultValue={htmlContent}
            placeholder={!readOnly && isPlaceholderVisible ? t(
              'COMPLEX_PAGE_OPTIONS:EMPTY_CREATIVE_BLOCK_PLACEHOLDER',
              'Enter text...'
            ) : null}
            onChange={quillContentChangeHandler}
            onChangeSelection={quillContentSelectionHandler}
            onFocus={quillFocusHandler}
            onBlur={quillBlurHandler}
          />
        </>
      )}
    </QuillContainer>
  );
});

HtmlBlockQuillRenderer.displayName = 'HtmlBlockQuillRenderer';

export default React.memo(withTranslation('COMPLEX_PAGE_OPTIONS')(HtmlBlockRenderer));
export { HtmlBlockQuillRenderer };
